// captureTrainingExample — fire-and-forget logger for AI interaction
// pairs we want in our future fine-tuning corpus.
//
// Each call records one row in ai_training_examples with:
//   - input         what the model received
//   - model_output  what the model returned
//   - user_final    what the user ended up with (null when accepted)
//   - correction_kind   accept | edit | regenerate | reject
//   - model_version     model + prompt version when known
//
// The diff between model_output and user_final is the training signal —
// that's what the model SHOULD have produced. When correction_kind is
// "accept" we still record (positive signal for the same model_output).
//
// Failure is non-fatal: capture is observability, not blocking. Any
// error logs to console.warn and the user flow continues. Never let
// a network blip on this path break a user action.

import { supabase } from "./supabase";

/**
 * @param {object} args
 * @param {string} args.userId      — caller's auth.uid()
 * @param {string} args.taskType    — discriminator (recipe_parse,
 *                                    photo_identify, recipe_generate,
 *                                    ingredient_swap, transcript_correct,
 *                                    etc.). Free-text by design — adding
 *                                    a new surface doesn't need a schema
 *                                    migration.
 * @param {object} args.input       — what the model received
 * @param {object} [args.modelOutput]  — what the model produced
 * @param {object} [args.userFinal]    — what the user ended with after
 *                                       any edits. Null = accepted as-is.
 * @param {"accept"|"edit"|"regenerate"|"reject"} args.correctionKind
 * @param {string} [args.modelVersion]  — "claude-sonnet-4-6@v5" etc.
 * @param {object} [args.metadata]      — per-task context (canonical
 *                                        roster size, user prefs, etc.)
 */
export async function captureTrainingExample({
  userId,
  taskType,
  input,
  modelOutput     = null,
  userFinal       = null,
  correctionKind  = "accept",
  modelVersion    = null,
  metadata        = null,
} = {}) {
  if (!userId || !taskType || !input) {
    console.warn("[capture-training] missing required field", { userId, taskType, input });
    return;
  }
  try {
    const { error } = await supabase
      .from("ai_training_examples")
      .insert({
        user_id:         userId,
        task_type:       taskType,
        input,
        model_output:    modelOutput,
        user_final:      userFinal,
        correction_kind: correctionKind,
        model_version:   modelVersion,
        metadata:        metadata || {},
      });
    if (error) {
      console.warn("[capture-training] insert failed:", error.message);
    }
  } catch (e) {
    console.warn("[capture-training] threw:", e?.message || String(e));
  }
}
